import { useState, type ReactNode } from 'react';
import { isAxiosError } from 'axios';
import { CalendarDays, Plus } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import {
  usePerDiemWorkers,
  usePerDiemEntries,
  useBulkCreatePerDiem,
  useSubmitPerDiem,
  useApprovePerDiem,
  usePayrollSettings,
  useTechPayRates,
} from '@/hooks/use-payroll';
import { useMyPayRate } from '@/hooks/use-my-pay-rate';
import { useAuth } from '@/contexts/auth-context';
import { useProjects } from '@/hooks/use-projects';
import { usePlatformModules } from '@/hooks/use-modules';
import { usePermissions } from '@/hooks/use-permissions';
import { useRejectPerDiem } from '@/hooks/use-per-diem-actions';
import { ApprovalError, SelfApprovedBadge, useSelfApproval } from '@/app/components/payroll/self-approval';
import { PersonSearchComboBox, type PersonOption } from '@/components/shared/person-search-combo-box';
import { useOrgCurrency, useOrgToday } from '@/hooks/use-settings';
import { formatMoney } from '@/lib/currency';
import type { PerDiemStatus } from '@/lib/types';

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  submitted: 'bg-blue-100 text-blue-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  reimbursed: 'bg-purple-100 text-purple-800',
};

function getBulkCreateErrorMessage(error: unknown): string {
  const fallback = 'Could not generate per diem entries. Please try again.';
  if (!isAxiosError(error)) return fallback;

  const responseData = error.response?.data;
  if (!responseData || typeof responseData !== 'object') return fallback;
  const data = responseData as Record<string, unknown>;

  if (typeof data.error === 'string') return data.error;
  if (typeof data.detail === 'string') return data.detail;
  if (Array.isArray(data.detail) && typeof data.detail[0] === 'string') {
    return data.detail[0];
  }

  for (const value of Object.values(data)) {
    if (typeof value === 'string') return value;
    if (Array.isArray(value) && typeof value[0] === 'string') {
      return value[0];
    }
  }
  return fallback;
}

interface RateFormProps {
  person: string;
  asOf: string;
  pending: boolean;
  formError?: string | null;
  children: ReactNode;
  onGenerate: () => Promise<void>;
}

interface ResolvedRateFormProps extends RateFormProps {
  defaultRate?: string;
  source?: string;
  loading: boolean;
}

function ResolvedRateForm({ person, asOf, defaultRate, source, loading, pending, formError, onGenerate, children }: ResolvedRateFormProps) {
  const context = `${person}:${asOf}:${defaultRate}`;
  const [confirmedContext, setConfirmedContext] = useState<string | null>(null);
  const zeroConfirmed = confirmedContext === context;
  const isZero = defaultRate !== undefined && defaultRate !== '' && Number(defaultRate) === 0;
  const canGenerate = !loading && !pending && (!isZero || zeroConfirmed);

  return (
    <form className="space-y-4" onSubmit={(event) => {
      event.preventDefault();
      if (!canGenerate) return;
      void onGenerate();
    }}>
      {children}
      <div className="max-w-sm space-y-1">
        <Label htmlFor="per-diem-rate">Daily Rate</Label>
        <Input
          id="per-diem-rate"
          readOnly
          value={defaultRate ?? ''}
          aria-describedby="per-diem-rate-source"
        />
        <p id="per-diem-rate-source" className="text-sm text-gray-600">
          {loading ? 'Loading effective rate...' : source ?? 'from Pay Rates'}
        </p>
      </div>
      {isZero && (
        <div className="space-y-2">
          <p role="alert" className="text-sm text-amber-800">The daily rate from Pay Rates is 0.00. Confirm zero-value entries.</p>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={zeroConfirmed} onChange={(event) => setConfirmedContext(event.target.checked ? context : null)} />
            I confirm generating entries at 0.00
          </label>
        </div>
      )}
      <Button type="submit" disabled={!canGenerate}>
        {pending ? 'Generating...' : 'Generate Entries'}
      </Button>
      {formError && <p role="alert" className="text-sm text-red-700">{formError}</p>}
    </form>
  );
}

function OtherWorkerRateForm(props: RateFormProps) {
  const today = useOrgToday();
  const { data: rates, isPending, isError } = useTechPayRates({
    person: Number(props.person),
    project: 'none',
  });
  const { data: settings, isPending: settingsPending, isError: settingsError } = usePayrollSettings();
  const rate = rates?.filter((item) => item.project == null && item.person === Number(props.person) && item.effective_date <= (props.asOf || today))
    .sort((a, b) => b.effective_date.localeCompare(a.effective_date))[0];
  const fromPayRates = rate?.per_diem_rate != null;
  const defaultRate = isError ? undefined : rate?.per_diem_rate ?? (settingsError ? undefined : settings?.default_per_diem_rate);
  return <ResolvedRateForm
    key={`${props.person}:${props.asOf}:${defaultRate}`}
    {...props}
    defaultRate={defaultRate}
    source={defaultRate === undefined ? undefined : fromPayRates ? 'from Pay Rates' : 'from Pay Rates (company default)'}
    loading={isPending || (!fromPayRates && settingsPending)}
  />;
}

function PerDiemRateForm(props: RateFormProps) {
  const { user } = useAuth();
  const permissions = usePermissions();
  const isSelf = Number(props.person) === user?.person_id;
  const { data, isFetching, isError } = useMyPayRate(props.asOf, isSelf);
  if (!isSelf && props.person && permissions.hasMinModuleRole('payroll', 'manager')) {
    return <OtherWorkerRateForm {...props} />;
  }
  const defaultRate = isSelf && !isError ? data?.per_diem_rate ?? data?.fallback_rate : undefined;
  return <ResolvedRateForm
    key={`${props.person}:${props.asOf}:${defaultRate}`}
    {...props}
    defaultRate={defaultRate}
    source={defaultRate === undefined ? undefined : data?.per_diem_rate != null ? 'from Pay Rates' : 'from Pay Rates (company default)'}
    loading={isSelf && isFetching}
  />;
}

export function PerDiemPage() {
  const { user } = useAuth();
  const orgCurrency = useOrgCurrency();
  const permissions = usePermissions();
  const isManager = permissions.canManagePayroll;

  const [statusFilter, setStatusFilter] = useState<PerDiemStatus | ''>('');
  const [personFilter, setPersonFilter] = useState<PersonOption | null>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showBulkForm, setShowBulkForm] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const { isModuleEnabled } = usePlatformModules();
  const { data: entries, isLoading } = usePerDiemEntries({
    status: statusFilter || undefined,
    ...(isManager
      ? {
          person: personFilter?.person_id ?? undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
        }
      : {}),
  });
  const { data: workers } = usePerDiemWorkers(showBulkForm);
  const { data: projects } = useProjects({ page_size: 100 }, isModuleEnabled('projects'));

  const bulkCreate = useBulkCreatePerDiem();
  const submitPerDiem = useSubmitPerDiem();
  const approvePerDiem = useApprovePerDiem();
  const selfApproval = useSelfApproval('per diem');
  const rejectPerDiem = useRejectPerDiem();
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectError, setRejectError] = useState<string | null>(null);
  const [form, setForm] = useState({
    person: user?.person_id ? String(user.person_id) : '',
    project: '',
    date_from: '',
    date_to: '',
    meals_included: false,
    lodging_included: false,
    notes: '',
  });

  const handleBulkCreate = async () => {
    if (!form.person || !form.date_from || !form.date_to) return;
    setFormError(null);
    setNotice(null);
    try {
      const result = await bulkCreate.mutateAsync({
        person: Number(form.person),
        project: form.project ? Number(form.project) : undefined,
        date_from: form.date_from,
        date_to: form.date_to,
        meals_included: form.meals_included,
        lodging_included: form.lodging_included,
        notes: form.notes || undefined,
      });
      if (result.created_count === 0) {
        setFormError(
          `No entries created: every date already has a per diem (${result.skipped_dates.join(', ')}).`,
        );
        return;
      }
      if (result.skipped_count > 0) {
        setNotice(
          `Created ${result.created_count} per diem entries. Skipped dates that already had one: ${result.skipped_dates.join(', ')}.`,
        );
      }
      setShowBulkForm(false);
    } catch (error) {
      setFormError(getBulkCreateErrorMessage(error));
    }
  };

  const handleReject = async (id: number) => {
    setRejectError(null);
    try {
      await rejectPerDiem.mutateAsync({ id, reason: rejectReason.trim() });
      setRejectingId(null);
      setRejectReason('');
    } catch (error) {
      const responseError =
        isAxiosError(error) &&
        error.response?.data &&
        typeof error.response.data === 'object' &&
        'error' in error.response.data &&
        typeof error.response.data.error === 'string'
          ? error.response.data.error
          : null;
      setRejectError(
        responseError ??
          (error instanceof Error
            ? error.message
            : 'Failed to reject per diem entry.'),
      );
    }
  };

  const cancelReject = () => {
    setRejectingId(null);
    setRejectReason('');
    setRejectError(null);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {selfApproval.dialog}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Per Diem</h1>
          <p className="text-gray-600 mt-1">Daily allowance tracking</p>
        </div>
        <Button
          onClick={() => {
            setFormError(null);
            setNotice(null);
            setShowBulkForm(!showBulkForm);
          }}
        >
          {!showBulkForm && <Plus className="h-4 w-4 mr-2" />}
          {showBulkForm ? 'Cancel' : 'Generate Per Diems'}
        </Button>
      </div>

      {/* Bulk Creation Form */}
      {showBulkForm && (
        <Card>
          <CardHeader>
            <CardTitle>Generate Per Diem Entries</CardTitle>
          </CardHeader>
          <CardContent>
            <PerDiemRateForm person={form.person} asOf={form.date_from} pending={bulkCreate.isPending} formError={formError} onGenerate={handleBulkCreate}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <Label>Worker *</Label>
                  <Select value={form.person} onValueChange={(v) => setForm({ ...form, person: v })}>
                    <SelectTrigger aria-label="Worker">
                      <SelectValue placeholder="Select worker" />
                    </SelectTrigger>
                    <SelectContent>
                      {workers?.map((worker) => (
                        <SelectItem key={worker.id} value={String(worker.id)}>
                          {worker.full_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Project</Label>
                  <Select value={form.project || 'none'} onValueChange={(v) => setForm({ ...form, project: v === 'none' ? '' : v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Optional" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {projects?.results?.map((proj) => (
                        <SelectItem key={proj.id} value={String(proj.id)}>
                          {proj.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="per-diem-start">Start Date *</Label>
                  <Input
                    id="per-diem-start"
                    type="date"
                    required
                    value={form.date_from}
                    onChange={(e) => setForm({ ...form, date_from: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="per-diem-end">End Date *</Label>
                  <Input
                    id="per-diem-end"
                    type="date"
                    required
                    value={form.date_to}
                    onChange={(e) => setForm({ ...form, date_to: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.meals_included}
                    onChange={(e) => setForm({ ...form, meals_included: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <span className="text-sm">Meals included by client</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.lodging_included}
                    onChange={(e) => setForm({ ...form, lodging_included: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <span className="text-sm">Lodging included by client</span>
                </label>
              </div>
            </PerDiemRateForm>
          </CardContent>
        </Card>
      )}

      {notice && (
        <p role="status" className="text-sm text-green-700">
          {notice}
        </p>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v as PerDiemStatus | '')}>
          <SelectTrigger className="w-48" aria-label="Status filter">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="reimbursed">Reimbursed</SelectItem>
          </SelectContent>
        </Select>
        {isManager && (
          <>
            <PersonSearchComboBox
              value={personFilter}
              onChange={(option) => setPersonFilter(option?.person_id != null ? option : null)}
              placeholder="Search workers to filter..."
              endpoint="persons"
              className="w-64 mt-0"
            />
            <Input
              type="date"
              aria-label="From date"
              className="w-44"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
            <Input
              type="date"
              aria-label="To date"
              className="w-44"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </>
        )}
      </div>

      {/* Per Diem List */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Loading per diem entries...</div>
          ) : !(Array.isArray(entries) ? entries : []).length ? (
            <div className="p-8 text-center text-gray-500">
              <CalendarDays className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No per diem entries found</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {(Array.isArray(entries) ? entries : []).map((entry) => (
                <div key={entry.id} className="flex items-center justify-between p-4 hover:bg-gray-50">
                  <div>
                    <p className="font-medium text-gray-900">{entry.person_name ?? entry.tech_name ?? 'Unknown'}</p>
                    <p className="text-sm text-gray-500">
                      {entry.date}
                      {entry.project_name && ` · ${entry.project_name}`}
                      {entry.meals_included && ' · Meals included'}
                      {entry.lodging_included && ' · Lodging included'}
                    </p>
                    {entry.status === 'rejected' && entry.rejection_reason && (
                      <p className="text-sm text-red-700">
                        Rejection reason: {entry.rejection_reason}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="font-semibold">
                        {formatMoney(entry.adjusted_rate, orgCurrency)}
                      </p>
                      {entry.rate !== entry.adjusted_rate && (
                        <p className="text-xs text-gray-400 line-through">
                          {formatMoney(entry.rate, orgCurrency)}
                        </p>
                      )}
                    </div>
                    <Badge className={statusColors[entry.status] ?? 'bg-gray-100 text-gray-800'}>
                      {entry.status}
                    </Badge>
                    <SelfApprovedBadge selfApproved={entry.self_approved} />
                    <div className="flex flex-wrap items-center gap-1">
                      {entry.status === 'draft' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => submitPerDiem.mutate(entry.id)}
                        >
                          Submit
                        </Button>
                      )}
                      {isManager && entry.status === 'submitted' && (
                        rejectingId === entry.id ? (
                          <div className="flex flex-wrap items-center gap-2">
                            <Input
                              aria-label="Rejection reason"
                              placeholder="Rejection reason..."
                              value={rejectReason}
                              onChange={(event) => {
                                setRejectReason(event.target.value);
                                setRejectError(null);
                              }}
                              className="w-48"
                            />
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => void handleReject(entry.id)}
                              disabled={
                                rejectPerDiem.isPending || !rejectReason.trim()
                              }
                            >
                              Confirm
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={cancelReject}
                            >
                              Cancel
                            </Button>
                            {rejectError && (
                              <p role="alert" className="basis-full text-sm text-red-700">
                                {rejectError}
                              </p>
                            )}
                          </div>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                void selfApproval.approve(entry.id, (confirmSelfApproval) =>
                                  approvePerDiem.mutateAsync({ id: entry.id, confirmSelfApproval }),
                                )
                              }
                            >
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600"
                              onClick={() => {
                                setRejectingId(entry.id);
                                setRejectReason('');
                                setRejectError(null);
                              }}
                            >
                              Reject
                            </Button>
                          </>
                        )
                      )}
                      <ApprovalError
                        message={selfApproval.errorFor(entry.id)}
                        className="basis-full"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
